import { shipmentStatusOptions } from '@/constants';

import type { AnimalRow } from './index';

export type DummyShipmentStatusValue =
  | 100000000
  | 100000001
  | 100000002
  | 100000007;

export type DummyShipment = {
  id: string;
  shipmentNumber: string;
  customer: string;
  origin: string;
  destination: string;
  status: DummyShipmentStatusValue;
  animalIds: string[];
};

export type ShipmentConflict = {
  animal: AnimalRow;
  shipment: DummyShipment;
};

export type ShipmentOverrideAuditPayload = {
  userIdentity: string;
  timestamp: string;
  sourceShipment: Pick<DummyShipment, 'id' | 'shipmentNumber' | 'status'>;
  destinationShipment: Pick<DummyShipment, 'id' | 'shipmentNumber' | 'status'>;
  affectedAnimals: Array<Pick<AnimalRow, 'id' | 'name' | 'nameTag'>>;
};

export type AddAnimalsToShipmentPayload = {
  shipmentId: string;
  animals: AnimalRow[];
  overrideConflicts?: boolean;
  nextStatus?: Extract<DummyShipmentStatusValue, 100000001 | 100000007>;
  userIdentity?: string;
};

export type AddAnimalsToShipmentResult = {
  shipment: DummyShipment;
  addedAnimals: AnimalRow[];
  movedAnimals: AnimalRow[];
  auditPayload?: ShipmentOverrideAuditPayload;
};

const SHIPPED = 100000000 as const;
const SCHEDULED = 100000001 as const;
const IN_PROGRESS = 100000002 as const;
const INCOMPLETE = 100000007 as const;

const shipmentStatusLabels = new Map(
  shipmentStatusOptions.map((option) => [option.value, option.label]),
);

let shipmentsData: DummyShipment[] = [
  {
    id: 'shipment-001',
    shipmentNumber: 'SHP-24018',
    customer: 'Blue Ridge Genetics',
    origin: 'Sunny Valley Ranch',
    destination: 'Madison, WI',
    status: SCHEDULED,
    animalIds: ['R-002'],
  },
  {
    id: 'shipment-002',
    shipmentNumber: 'SHP-24021',
    customer: 'Prairie View Embryos',
    origin: 'Green Pastures Farm',
    destination: 'Omaha, NE',
    status: INCOMPLETE,
    animalIds: ['C-007'],
  },
  {
    id: 'shipment-003',
    shipmentNumber: 'SHP-24011',
    customer: 'Summit Transfer Co.',
    origin: 'Highland Ranch',
    destination: 'Sioux Falls, SD',
    status: IN_PROGRESS,
    animalIds: ['D-001', 'D-006'],
  },
  {
    id: 'shipment-004',
    shipmentNumber: 'SHP-23990',
    customer: 'West Fork Cattle',
    origin: 'Sunrise Farm',
    destination: 'Amarillo, TX',
    status: SHIPPED,
    animalIds: ['R-005'],
  },
  {
    id: 'shipment-005',
    shipmentNumber: 'SHP-24026',
    customer: 'Northern Plains Donors',
    origin: 'Meadow Creek Ranch',
    destination: 'Fargo, ND',
    status: SCHEDULED,
    animalIds: [],
  },
];

const matchesShipmentSearch = (shipment: DummyShipment, search: string) => {
  if (!search.trim()) {
    return true;
  }

  const query = search.trim().toLowerCase();
  return [
    shipment.shipmentNumber,
    shipment.customer,
    shipment.origin,
    shipment.destination,
    getShipmentStatusLabel(shipment.status),
  ].some((value) => value.toLowerCase().includes(query));
};

export function getShipmentStatusLabel(status: DummyShipmentStatusValue) {
  return shipmentStatusLabels.get(status) ?? 'Unknown';
}

export function getShipments(search = ''): DummyShipment[] {
  return shipmentsData
    .filter((shipment) => matchesShipmentSearch(shipment, search))
    .toSorted((a, b) => a.shipmentNumber.localeCompare(b.shipmentNumber));
}

export function getShipmentById(id: string) {
  return shipmentsData.find((shipment) => shipment.id === id) ?? null;
}

export function getShipmentConflicts(
  animals: AnimalRow[],
  destinationShipmentId?: string,
): ShipmentConflict[] {
  const animalsById = new Map(animals.map((animal) => [animal.id, animal]));

  return shipmentsData.flatMap((shipment) => {
    if (
      shipment.status !== IN_PROGRESS ||
      shipment.id === destinationShipmentId
    ) {
      return [];
    }

    return shipment.animalIds.flatMap((animalId) => {
      const animal = animalsById.get(animalId);
      return animal ? [{ animal, shipment }] : [];
    });
  });
}

export function addAnimalsToShipment({
  shipmentId,
  animals,
  overrideConflicts = false,
  nextStatus,
  userIdentity = 'Unknown User',
}: AddAnimalsToShipmentPayload): AddAnimalsToShipmentResult {
  const shipment = getShipmentById(shipmentId);

  if (!shipment) {
    throw new Error('Shipment not found');
  }

  const conflicts = getShipmentConflicts(animals, shipmentId);
  if (conflicts.length > 0 && !overrideConflicts) {
    throw new Error(
      'Override confirmation is required for conflicting animals',
    );
  }

  const movedAnimalIds = new Set(
    conflicts.map((conflict) => conflict.animal.id),
  );

  if (movedAnimalIds.size > 0) {
    shipmentsData = shipmentsData.map((currentShipment) => {
      if (currentShipment.id === shipment.id) {
        return currentShipment;
      }

      return {
        ...currentShipment,
        animalIds: currentShipment.animalIds.filter(
          (animalId) => !movedAnimalIds.has(animalId),
        ),
      };
    });
  }

  const animalIdsToAdd = animals.map((animal) => animal.id);

  shipmentsData = shipmentsData.map((currentShipment) => {
    if (currentShipment.id !== shipment.id) {
      return currentShipment;
    }

    const nextAnimalIds = new Set(currentShipment.animalIds);
    animalIdsToAdd.forEach((animalId) => nextAnimalIds.add(animalId));

    return {
      ...currentShipment,
      status: nextStatus ?? currentShipment.status,
      animalIds: [...nextAnimalIds],
    };
  });

  const updatedShipment = getShipmentById(shipment.id)!;

  return {
    shipment: updatedShipment,
    addedAnimals: animals.filter((animal) => !movedAnimalIds.has(animal.id)),
    movedAnimals: animals.filter((animal) => movedAnimalIds.has(animal.id)),
    auditPayload:
      movedAnimalIds.size > 0
        ? {
            userIdentity,
            timestamp: new Date().toISOString(),
            sourceShipment: {
              id: conflicts[0].shipment.id,
              shipmentNumber: conflicts[0].shipment.shipmentNumber,
              status: conflicts[0].shipment.status,
            },
            destinationShipment: {
              id: updatedShipment.id,
              shipmentNumber: updatedShipment.shipmentNumber,
              status: updatedShipment.status,
            },
            affectedAnimals: conflicts.map(({ animal }) => ({
              id: animal.id,
              name: animal.name,
              nameTag: animal.nameTag,
            })),
          }
        : undefined,
  };
}

export const shipmentStatusChangeOptions = shipmentStatusOptions.filter(
  (option): option is { label: string; value: 100000001 | 100000007 } =>
    option.value === SCHEDULED || option.value === INCOMPLETE,
);
